import React from "react"
import { Paper, makeStyles } from "@material-ui/core"
import type { ReportedHourViewModel } from "../../hour-reports/api"
import { makeOnSearchChange } from "../base/event-handlers/on-search-change"
import { useTranslations } from "../hooks/use-translations"
import { useTreeGridFilters } from "../hooks/use-treegrid-filters"
import { createTreegrid, destroyTreegrid, updateGridData } from "../utils/tree-grid"
import { ReportedHoursGridConfig, resolveReportedHoursColumnSettings } from "./column-settings"
import { makeLayout, mapGroupByToColumn, TOTAL_HOURS_ROW_ID } from "./layout"
import { makeTreeGridReportedHourRows } from "./rows"
import { ReportedHoursGroupBy } from "./types"
import { convertDecimalHoursToHoursAndMinutes } from "../../hour-reports/utils"
import type { HourReportingWorkflowSettings } from "../../hour-reports/hooks/use-hour-reporting-workflow-settings"
import { mapReportedHoursGroupByValue } from "./grouping"
import { useHourReportTranslations } from "../hooks/use-hour-report-translations"

const id = "__treegrid_reported_hours__"

type ReportedHoursTableProps = {
  reports: ReportedHourViewModel[]
  dateFormat: string
  dateSeparator: string
  firstDayOfWeek: DayOfWeek
  gridInfo: string
  gridConfig: ReportedHoursGridConfig
  workflowSettings?: HourReportingWorkflowSettings
}

const ReportedHoursTable = (props: ReportedHoursTableProps) => {
  const classes = useStyles()
  const translations = useTranslations()
  const { noDataText, labels, statuses, notSelected } = useHourReportTranslations()
  const { reports, dateFormat, dateSeparator, firstDayOfWeek, gridInfo, gridConfig, workflowSettings } = props
  const supportsWorkflowStatus = workflowSettings?.supportsSubmission ?? true
  const hideTimerColumns = reports.length > 0 && reports.every((report) => report.method === "manual")
  const { order: columnOrder, visible: visibleColumns } = React.useMemo(
    () =>
      resolveReportedHoursColumnSettings({
        columnSettings: gridConfig.columnSettings,
        supportsWorkflowStatus,
        hideTimerColumns,
      }),
    [gridConfig.columnSettings, hideTimerColumns, supportsWorkflowStatus]
  )
  const { showFilters, toggleFilters } = useTreeGridFilters({ gridId: id })
  const [groupBy, setGroupBy] = React.useState(ReportedHoursGroupBy.NO_GROUPING)
  const resolvedGroupBy = mapReportedHoursGroupByValue(groupBy, { availableColumns: columnOrder })
  const data = React.useMemo(
    () => makeTreeGridReportedHourRows({ reports, dateFormat, workflowSettings, statusLabels: statuses, notSelected }),
    [dateFormat, notSelected, reports, statuses, workflowSettings]
  )
  const layout = React.useMemo(
    () =>
      makeLayout({
        id,
        cfgId: `${id}-${gridConfig.id}`,
        dateFormat,
        dateSeparator,
        firstDayOfWeek,
        gridInfo,
        groupBy: resolvedGroupBy,
        noDataText,
        showFilters,
        translations,
        columnOrder,
        visibleColumns,
        supportsWorkflowStatus,
        labels,
      }),
    [
      columnOrder,
      dateFormat,
      dateSeparator,
      firstDayOfWeek,
      gridInfo,
      resolvedGroupBy,
      noDataText,
      visibleColumns,
      showFilters,
      translations,
      supportsWorkflowStatus,
      gridConfig.id,
      labels,
    ]
  )
  const onSearchChange = React.useMemo(() => makeOnSearchChange({ translations }), [translations])

  // @ts-ignore
  window.Grids.OnFilterToggle = function () {
    toggleFilters()
  }

  window.Grids.OnFilterFinish = function (grid: TGrid) {
    if (!grid) return

    const totalHoursRow = grid.GetRowById(TOTAL_HOURS_ROW_ID)
    if (!totalHoursRow) return

    /**
     * Doing this somehow recalculates the hours cell for total hours row when column filter is applied
     * Without this, the total hours value remains the same as before applying the filter, which is incorrect.
     */
    grid.SetValue(totalHoursRow, "hours", "")
    grid.RefreshCell(totalHoursRow, "hours")
  }

  // @ts-ignore
  window.Grids.OnSelectGroupByChange = function (grid: TGrid, groupByValue: string) {
    if (!grid) return
    const selectedGroupBy = (Object.entries(labels.groupBy).find(([, label]) => label === groupByValue)?.[0] ??
      groupByValue) as string
    const nextGroupBy = mapReportedHoursGroupByValue(selectedGroupBy, {
      availableColumns: columnOrder,
    })
    setGroupBy(nextGroupBy)
    grid.DoGrouping(mapGroupByToColumn(nextGroupBy))
    hideInternalGroupColumns(grid)
  }

  // @ts-expect-error TreeGrid event hooks are stored on the global Grids object.
  window.Grids.OnSearchChange = function (grid: TGrid, searchQuery: string) {
    onSearchChange(grid, searchQuery)
  }

  // @ts-expect-error - This function is used to format total reported hours in Column Formula
  window.Grids.FormatTotalReportedHours = function (value: number) {
    const { hours, minutes } = convertDecimalHoursToHoursAndMinutes(value)
    return `${hours}h ${minutes}m`
  }

  React.useEffect(() => {
    createTreegrid({ id, data, layout })
    return () => destroyTreegrid(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridConfig.id, supportsWorkflowStatus])

  React.useEffect(() => {
    updateGridData({ id, data })
  }, [data])

  return (
    <Paper variant="outlined" elevation={0} className={classes.paper}>
      <div id={id} className={classes.grid} />
    </Paper>
  )
}

const useStyles = makeStyles({
  paper: {
    position: "relative",
    width: "100%",
  },
  grid: {
    height: "calc(100vh - 340px)",
    minHeight: 360,
  },
})

const hideInternalGroupColumns = (grid: NonNullable<TGrid>) => {
  grid.HideCol("project")
}

export default ReportedHoursTable
